// Evidence composition — always prefer the authoritative value the
// backend persists at publish-time. Fall back to a derivation from the
// on-page surfaces only when the API doesn't provide one (legacy rows).
//
// Authoritative source: `opportunity.meta.evidenceComposition` — set by
// the seed and by any future opportunity coming from the AI pipeline
// (RSS → analyze → publish), where the counts come from the
// intelligence agent's `field_evidence` rather than derived counts.
//
// Derivation fallback:
//   • verified          = citations in `sources` (editorial refs)
//   • estimated         = quantified metrics in `keyIndicators`
//   • expert_assessment = risk-level judgments in `risks`
//   • unknown           = 0
//
// If neither is available, returns null so the UI can hide the composition
// block entirely (per the "no fake counts" contract).

const _shape = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const verified          = Number(raw.verified          || 0);
  const estimated         = Number(raw.estimated         || 0);
  const expert_assessment = Number(raw.expert_assessment || 0);
  const unknown           = Number(raw.unknown           || 0);
  const total             = raw.total != null
    ? Number(raw.total)
    : verified + estimated + expert_assessment + unknown;
  if (total === 0) return null;
  return {
    verified,
    estimated,
    expert_assessment,
    unknown,
    total,
    source: raw._source || "authoritative",
  };
};

export const deriveEvidenceComposition = (opportunity) => {
  if (!opportunity) return null;

  // 1. Prefer the authoritative field the backend persists.
  const authoritative = opportunity.meta?.evidenceComposition
                     ?? opportunity.evidenceComposition;
  const persisted = _shape(authoritative);
  if (persisted) return persisted;

  // 2. Fall back to a derivation from on-page surfaces (legacy rows).
  const sources    = Array.isArray(opportunity.sources)       ? opportunity.sources       : [];
  const indicators = Array.isArray(opportunity.keyIndicators) ? opportunity.keyIndicators : [];
  const risks      = Array.isArray(opportunity.risks) ? opportunity.risks
                    : Array.isArray(opportunity.meta?.risks) ? opportunity.meta.risks : [];

  return _shape({
    verified:          sources.length,
    estimated:         indicators.length,
    expert_assessment: risks.length,
    unknown:           0,
    _source:           "derived",
  });
};

/** Priority: verified > estimated > expert_assessment > unknown. */
export const dominantEvidenceClass = (composition) => {
  if (!composition) return null;
  if (composition.verified >= composition.estimated
      && composition.verified >= composition.expert_assessment) return "verified";
  if (composition.estimated >= composition.expert_assessment)   return "estimated";
  if (composition.expert_assessment > 0)                        return "expert_assessment";
  return "unknown";
};
